// src/utils/videoConverter.js
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

/**
 * Video converter utility to convert videos to 480p resolution with H.264 codec
 * Pure browser-based conversion - NO external services, NO paid APIs
 * 
 * About "Blob":
 * - "Blob" is a FREE browser API (not a paid service!)
 * - It's used to handle binary data (like video files) in the browser
 * - It's built into every modern browser - completely free
 * - We use it to temporarily store video data in browser memory
 * 
 * This converter works 100% in your browser - no server needed!
 */
class VideoConverter {
  constructor() {
    this.ffmpeg = null;
    this.isLoaded = false;
    this.loadPromise = null;
  }

  /**
   * Initialize FFmpeg (loads WebAssembly files)
   * Should be called once before using the converter
   * Uses direct CDN URLs - no blob URL conversion needed
   */
  async load() {
    if (this.isLoaded) {
      return;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = (async () => {
      try {
        console.log('🎬 Initializing FFmpeg (browser-based, 100% free)...');
        this.ffmpeg = new FFmpeg();
        
        // Set up logging
        this.ffmpeg.on('log', ({ message }) => {
          // Only log important messages to avoid console spam
          if (message && (
            message.toLowerCase().includes('error') ||
            message.toLowerCase().includes('warning') ||
            message.toLowerCase().includes('duration')
          )) {
            console.log('FFmpeg:', message);
          }
        });
        
        // Load FFmpeg directly from CDN - no blob URL conversion
        // This is more reliable and works in all browsers
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        
        console.log('📦 Loading FFmpeg core files from CDN...');
        console.log('⏱️ This may take 30-60 seconds on first load (downloading ~10MB)...');
        console.log('💡 Tip: FFmpeg only loads once - subsequent conversions will be faster!');
        console.log('⏳ No timeout - will wait as long as needed...');
        
        // Load FFmpeg - NO TIMEOUT (user requested no time limits)
        await this.ffmpeg.load({
          coreURL: `${baseURL}/ffmpeg-core.js`,
          wasmURL: `${baseURL}/ffmpeg-core.wasm`,
        });

        this.isLoaded = true;
        console.log('✅ FFmpeg loaded successfully - ready to convert videos!');
      } catch (error) {
        console.error('❌ Failed to load FFmpeg:', error);
        this.loadPromise = null;
        
        // Provide helpful error message
        let errorMsg = 'Failed to load FFmpeg. ';
        if (error.message) {
          errorMsg += error.message;
        } else {
          errorMsg += 'Please check your internet connection and try again.';
        }
        
        throw new Error(errorMsg);
      }
    })();

    return this.loadPromise;
  }

  /**
   * Detects video codec (for logging purposes)
   * @param {File} videoFile - The video file
   * @returns {Promise<string|null>} - Codec name or null
   */
  async detectVideoCodec(videoFile) {
    // For web, we can use HTML5 video element to get some info
    // But codec detection is complex, so we'll just return null and convert anyway
    return null;
  }

  /**
   * Detects video resolution
   * @param {File} videoFile - The video file
   * @returns {Promise<{width: number, height: number}|null>} - Resolution or null
   */
  async detectVideoResolution(videoFile) {
    try {
      // Create a video element to get resolution
      return new Promise((resolve) => {
        const video = document.createElement('video');
        const url = URL.createObjectURL(videoFile);
        
        video.onloadedmetadata = () => {
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
          });
          URL.revokeObjectURL(url);
        };
        
        video.onerror = () => {
          resolve(null);
          URL.revokeObjectURL(url);
        };
        
        video.src = url;
      });
    } catch (error) {
      console.warn('⚠️ Error detecting resolution:', error);
      return null;
    }
  }

  /**
   * Validates video file before conversion
   * @param {File} videoFile - The video file to validate
   * @throws {Error} If file is invalid
   */
  validateVideoFile(videoFile) {
    // Check file size (2GB = 2147483648 bytes)
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    if (videoFile.size > maxSize) {
      throw new Error(`File is too large (${(videoFile.size / (1024 * 1024 * 1024)).toFixed(2)}GB). Maximum allowed size is 2GB.`);
    }

    // Warn for large files (>1.5GB) but allow
    const warningSize = 1.5 * 1024 * 1024 * 1024; // 1.5GB
    if (videoFile.size > warningSize) {
      console.warn(`⚠️ Large file detected (${(videoFile.size / (1024 * 1024 * 1024)).toFixed(2)}GB). Conversion may take longer or fail due to browser memory limits.`);
    }

    // Check if file has a valid video extension
    const validExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv', '.3gp', '.m4v', '.wmv', '.mpg', '.mpeg'];
    const fileExtension = '.' + videoFile.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      console.warn(`⚠️ Uncommon file extension: ${fileExtension}. Conversion may still work if FFmpeg supports it.`);
    }

    // Check if file is empty
    if (videoFile.size === 0) {
      throw new Error('File is empty. Please select a valid video file.');
    }
  }

  /**
   * Extracts error message from FFmpeg error
   * @param {Error} error - The error object
   * @returns {string} - Human-readable error message
   */
  extractErrorMessage(error) {
    if (!error) {
      return 'Unknown error occurred during conversion.';
    }

    // If error has a message, use it
    if (error.message) {
      const msg = error.message.toLowerCase();
      
      // Check for specific error patterns
      if (msg.includes('out of memory') || msg.includes('memory') || msg.includes('allocation')) {
        return 'File is too large for browser memory. Please try a smaller video file (<1.5GB recommended).';
      }
      
      if (msg.includes('invalid data') || msg.includes('corrupt') || msg.includes('invalid file')) {
        return 'Video file appears to be corrupted or invalid. Please try a different file.';
      }
      
      if (msg.includes('format') || msg.includes('codec') || msg.includes('unsupported')) {
        return 'Video format or codec is not supported. Please try converting the video to MP4 format first.';
      }
      
      if (msg.includes('timeout') || msg.includes('time out')) {
        return 'Conversion timed out. The video file may be too large or complex. Please try a smaller file.';
      }
      
      if (msg.includes('network') || msg.includes('fetch')) {
        return 'Failed to load FFmpeg. Please check your internet connection and try again.';
      }
      
      // Return the original message if no pattern matches
      return error.message;
    }

    // If error is a string, return it
    if (typeof error === 'string') {
      return error;
    }

    // If error has toString, use it
    if (error.toString && error.toString() !== '[object Object]') {
      return error.toString();
    }

    // Last resort: return generic message
    return 'Video conversion failed. The file may be corrupted, too large, or in an unsupported format. Please try a different video file.';
  }

  /**
   * Checks if video needs conversion
   * Don't convert if width ≤ 480px OR height ≤ 480px (e.g., 480×848 portrait or 848×480 landscape)
   * @param {File} videoFile - The input video file
   * @returns {Promise<boolean>} - True if conversion is needed, false otherwise
   */
  async needsConversion(videoFile) {
    try {
      const resolution = await this.detectVideoResolution(videoFile);
      if (!resolution) {
        // If we can't detect resolution, convert to be safe
        console.warn('⚠️ Could not detect resolution, will convert to ensure compatibility');
        return true;
      }
      
      const { width, height } = resolution;
      
      // Don't convert if width ≤ 480px OR height ≤ 480px
      // This handles both portrait (480×848) and landscape (848×480) videos
      if (width <= 480 || height <= 480) {
        const orientation = width < height ? 'portrait' : width > height ? 'landscape' : 'square';
        console.log(`✅ Video resolution: ${width}×${height}px (${orientation})`);
        console.log(`✅ Video has a dimension ≤ 480px (width=${width}, height=${height}) - no conversion needed`);
        return false;
      } else {
        const orientation = width < height ? 'portrait' : width > height ? 'landscape' : 'square';
        console.log(`📐 Video resolution: ${width}×${height}px (${orientation})`);
        console.log(`📐 Both dimensions > 480px (width=${width}, height=${height}) - conversion needed`);
        return true;
      }
    } catch (error) {
      console.warn('⚠️ Error checking if conversion needed:', error);
      // If check fails, convert to be safe
      return true;
    }
  }

  /**
   * Converts a video file to 480p resolution with H.264 codec
   * ONLY converts if both width AND height are > 480px
   * Skips conversion if width ≤ 480px OR height ≤ 480px (e.g., 480×848 portrait or 848×480 landscape)
   * @param {File} videoFile - The input video file
   * @param {Function} onProgress - Progress callback (0-1)
   * @returns {Promise<Blob>} - Converted video as Blob (or original if no conversion needed)
   */
  async convertTo480p(videoFile, onProgress) {
    try {
      // Validate file before processing
      this.validateVideoFile(videoFile);

      console.log('🎬 Checking if video conversion is needed...');
      console.log('📁 Original file:', videoFile.name);
      console.log('📊 Original size:', (videoFile.size / (1024 * 1024)).toFixed(2), 'MB');

      // Check if conversion is needed (only if both width AND height > 480px)
      const needsConversion = await this.needsConversion(videoFile);
      
      if (!needsConversion) {
        console.log('✅ Video has a dimension ≤ 480px - no conversion needed, using original file');
        // Return original file as Blob - no conversion needed
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(new Blob([reader.result], { type: videoFile.type || 'video/mp4' }));
          };
          reader.onerror = () => {
            // Fallback: convert anyway if we can't read the file
            console.warn('⚠️ Error reading original file, will convert instead');
            this.performConversion(videoFile, onProgress).then(resolve).catch(reject => {
              throw new Error('Failed to use original file and conversion also failed');
            });
          };
          reader.readAsArrayBuffer(videoFile);
        });
      }

      // Video needs conversion (> 480p)
      return await this.performConversion(videoFile, onProgress);
    } catch (error) {
      console.error('❌ Video conversion error:', error);
      
      // Extract meaningful error message
      const errorMessage = this.extractErrorMessage(error);
      
      // Check for format/codec errors and provide better guidance
      const lowerError = errorMessage.toLowerCase();
      if (lowerError.includes('format') || lowerError.includes('codec') || lowerError.includes('unsupported')) {
        // Try to provide more helpful error message
        throw new Error('Video format or codec issue detected. FFmpeg should support most formats. The file may be corrupted or use a very rare codec. Please try:\n1. Opening the video in a video player to verify it plays\n2. Re-exporting the video from your video editor\n3. Trying a different video file');
      }
      
      // Create a more descriptive error
      const descriptiveError = new Error(errorMessage);
      descriptiveError.originalError = error;
      
      throw descriptiveError;
    }
  }

  /**
   * Performs the actual video conversion
   * @param {File} videoFile - The input video file
   * @param {Function} onProgress - Progress callback (0-1)
   * @returns {Promise<Blob>} - Converted video as Blob
   */
  async performConversion(videoFile, onProgress) {
    const startTime = Date.now(); // Track conversion start time
    try {
      await this.load();

      console.log('🎬 Starting video conversion to 480p H.264...');
      
      // Get original resolution
      const originalResolution = await this.detectVideoResolution(videoFile);
      if (originalResolution) {
        console.log(`📐 Original resolution: ${originalResolution.width}x${originalResolution.height}`);
      }

      // Set up progress tracking
      if (onProgress) {
        this.ffmpeg.on('progress', ({ progress }) => {
          if (onProgress && progress !== undefined) {
            onProgress(progress);
          }
        });
      }

      // Write input file
      const inputFileName = 'input.' + videoFile.name.split('.').pop();
      const outputFileName = 'output.mp4';
      
      const inputData = await fetchFile(videoFile);
      await this.ffmpeg.writeFile(inputFileName, inputData);

      // Build FFmpeg command for 480p H.264 conversion
      // YouTube-like aggressive compression for smaller file sizes
      // Enhanced to support ALL video formats and codecs
      // Uses auto-detection for input format/codec - works with ANY video format
      
      // Calculate adaptive compression settings based on original file size and resolution
      // YouTube-like aggressive compression for smaller file sizes
      const originalSizeMB = videoFile.size / (1024 * 1024);
      let targetBitrate = '600k'; // YouTube-like: lower bitrate for 480p (typically 500-800kbps)
      let crfValue = '30'; // YouTube-like: aggressive compression (CRF 30-32 for streaming)
      
      // Get resolution to estimate if file is already compressed
      const origRes = originalResolution;
      const isLowRes = origRes && origRes.height <= 720; // 720p or lower
      
      // Adjust bitrate and CRF based on original file size
      // YouTube-style: prioritize smaller file sizes with acceptable quality
      if (originalSizeMB > 500) {
        // Large files (>500MB): slightly higher bitrate but still aggressive
        targetBitrate = '800k';
        crfValue = '28'; // Still aggressive compression
        console.log(`📊 Large file detected (${originalSizeMB.toFixed(2)}MB) - Using YouTube-like compression`);
      } else if (originalSizeMB > 100) {
        // Medium files (100-500MB): YouTube-like settings
        targetBitrate = '700k';
        crfValue = '30';
        console.log(`📊 Medium file (${originalSizeMB.toFixed(2)}MB) - Using YouTube-like compression`);
      } else if (originalSizeMB < 10) {
        // Small files (<10MB): maximum compression like YouTube
        targetBitrate = '500k';
        crfValue = '32'; // Maximum compression for already small files
        console.log(`📊 Small file (${originalSizeMB.toFixed(2)}MB) - Using maximum YouTube-like compression`);
      } else {
        // Standard files (10-100MB): check if low resolution (already compressed)
        if (isLowRes) {
          // 720p or lower - already compressed, use very aggressive settings
          targetBitrate = '550k';
          crfValue = '31'; // Very aggressive for already-compressed videos
          console.log(`📊 Standard file (${originalSizeMB.toFixed(2)}MB) at ${origRes.width}x${origRes.height} - Using YouTube-like aggressive compression`);
        } else {
          // Higher resolution - YouTube-like settings
          targetBitrate = '650k';
          crfValue = '30';
          console.log(`📊 Standard file (${originalSizeMB.toFixed(2)}MB) - Using YouTube-like compression`);
        }
      }
      
      const bufsize = `${parseFloat(targetBitrate) * 2}M`; // Buffer size (2x maxrate)
      
      const args = [
        '-i', inputFileName,
        // Input options - allow any format/codec, analyze entire file if needed
        '-analyzeduration', '2147483647', // Analyze entire file if needed
        '-probesize', '2147483647', // Probe entire file if needed
        '-fflags', '+genpts', // Generate presentation timestamps if missing
        '-err_detect', 'ignore_err', // Ignore errors and continue
        // Video processing - auto-detect input, convert to H.264
        // YOUTUBE-LIKE COMPRESSION: Aggressive compression for smaller file sizes
        '-vf', 'scale=-2:480', // Scale to 480p height, maintain aspect ratio
        '-c:v', 'libx264', // H.264 codec (output)
        '-preset', 'fast', // Fast preset for faster conversion
        '-crf', crfValue, // YouTube-like aggressive compression (CRF 30-32)
        '-maxrate', targetBitrate, // YouTube-like bitrate limits (500-800kbps for 480p)
        '-bufsize', bufsize, // Buffer size (2x maxrate)
        '-profile:v', 'baseline', // Baseline profile for maximum compatibility
        '-level', '3.0', // H.264 level
        '-pix_fmt', 'yuv420p', // Pixel format for compatibility
        '-vsync', 'cfr', // Constant frame rate output
        '-g', '30', // Keyframe interval
        // Audio processing - handle any audio codec, convert to AAC
        '-c:a', 'aac', // AAC audio codec (output)
        '-b:a', '96k', // YouTube-like audio bitrate (64-96kbps for 480p)
        '-ar', '44100', // Audio sample rate
        '-ac', '2', // Audio channels (stereo)
        '-af', 'aresample=async=1', // Resample audio if needed
        // Output options
        '-movflags', '+faststart', // Enable fast start for web playback
        '-strict', '-2', // Allow experimental codecs if needed
        '-f', 'mp4', // Force MP4 format
        '-y', // Overwrite output file
        outputFileName,
      ];

      console.log('🔧 FFmpeg command:', args.join(' '));

      // Collect FFmpeg logs for error diagnosis
      const ffmpegLogs = [];
      const logHandler = ({ message }) => {
        if (message) {
          ffmpegLogs.push(message);
          // Check for common error patterns in logs
          const lowerMsg = message.toLowerCase();
          if (
            lowerMsg.includes('error') ||
            lowerMsg.includes('failed') ||
            lowerMsg.includes('invalid') ||
            lowerMsg.includes('cannot') ||
            lowerMsg.includes('unsupported')
          ) {
            console.error('FFmpeg error log:', message);
          }
        }
      };
      
      // Add log handler (we'll keep it for the session)
      this.ffmpeg.on('log', logHandler);

      // originalSizeMB was already calculated above, reuse it
      console.log(`⏱️ Converting ${originalSizeMB.toFixed(2)}MB file - NO TIMEOUT (will wait as long as needed)`);

      try {
        // Execute conversion - NO TIMEOUT (user requested no time limits)
        await this.ffmpeg.exec(args);
      } catch (execError) {
        // Check logs for error details
        const errorLogs = ffmpegLogs.filter(log => {
          if (!log) return false;
          const lowerLog = log.toLowerCase();
          return (
            lowerLog.includes('error') ||
            lowerLog.includes('failed') ||
            lowerLog.includes('invalid') ||
            lowerLog.includes('cannot') ||
            lowerLog.includes('unsupported') ||
            lowerLog.includes('codec') ||
            lowerLog.includes('format')
          );
        });
        
        if (errorLogs.length > 0) {
          console.error('FFmpeg error logs:', errorLogs);
          const lastError = errorLogs[errorLogs.length - 1];
          const errorPreview = lastError.length > 200 ? lastError.substring(0, 200) + '...' : lastError;
          
          // Create more descriptive error
          let descriptiveError = 'FFmpeg conversion failed. ';
          
          if (lastError.toLowerCase().includes('codec') || lastError.toLowerCase().includes('format')) {
            descriptiveError += 'The video codec or format may not be supported. Please try converting the video to MP4 format first.';
          } else if (lastError.toLowerCase().includes('memory') || lastError.toLowerCase().includes('allocation')) {
            descriptiveError += 'File is too large for browser memory. Please try a smaller video file (<1.5GB recommended).';
          } else {
            descriptiveError += errorPreview;
          }
          
          throw new Error(descriptiveError);
        }
        
        // If no specific error logs, use the original error
        throw execError;
      }

      // Read output file
      const data = await this.ffmpeg.readFile(outputFileName);
      
      // Check if output file is valid
      if (!data || data.length === 0) {
        throw new Error('Conversion produced an empty output file. The video format may not be supported.');
      }
      
      // Create Blob from converted video data
      // NOTE: "Blob" is a FREE browser API (not a paid service!)
      // It's used to handle binary data in memory - completely free and built into browsers
      const blob = new Blob([data], { type: 'video/mp4' });
      
      // Verify blob size is reasonable (at least 1KB)
      if (blob.size < 1024) {
        throw new Error('Converted video file is too small. The conversion may have failed.');
      }

      // Check conversion results and log detailed information
      // Note: originalSizeMB was already calculated earlier, reuse it
      const convertedSizeMB = blob.size / (1024 * 1024);
      
      console.log(`\n📊 CONVERSION SUMMARY:`);
      console.log(`   Original: ${originalSizeMB.toFixed(2)}MB`);
      console.log(`   Converted: ${convertedSizeMB.toFixed(2)}MB`);
      
      // CRITICAL: If converted file is larger, re-convert with more aggressive compression
      if (blob.size > videoFile.size) {
        console.warn(`⚠️ WARNING: Converted file (${convertedSizeMB.toFixed(2)}MB) is LARGER than original (${originalSizeMB.toFixed(2)}MB)`);
        console.warn('⚠️ Original video was already well-compressed. Re-converting with more aggressive settings...');
        
        // Cleanup first conversion
        try {
          await this.ffmpeg.deleteFile(inputFileName);
          await this.ffmpeg.deleteFile(outputFileName);
        } catch (e) {
          console.warn('⚠️ Error cleaning up temp files:', e);
        }
        
        // IMPORTANT: Fetch file fresh - inputData may be detached after first conversion
        // ArrayBuffers transferred to Workers become detached and can't be reused
        const freshInputData = await fetchFile(videoFile);
        
        // Re-convert with maximum YouTube-like compression settings
        const aggressiveArgs = [
          '-i', inputFileName,
          '-analyzeduration', '2147483647',
          '-probesize', '2147483647',
          '-fflags', '+genpts',
          '-err_detect', 'ignore_err',
          '-vf', 'scale=-2:480',
          '-c:v', 'libx264',
          '-preset', 'fast', // Fast preset for faster conversion
          '-crf', '32', // Maximum YouTube-like compression (CRF 32)
          '-maxrate', '500k', // Minimum YouTube-like bitrate
          '-bufsize', '1M',
          '-profile:v', 'baseline',
          '-level', '3.0',
          '-pix_fmt', 'yuv420p',
          '-vsync', 'cfr',
          '-g', '30',
          '-c:a', 'aac',
          '-b:a', '64k', // YouTube-like minimum audio bitrate
          '-ar', '44100',
          '-ac', '2',
          '-af', 'aresample=async=1',
          '-movflags', '+faststart',
          '-strict', '-2',
          '-f', 'mp4',
          '-y',
          outputFileName,
        ];
        
        // Write input file again with fresh data
        await this.ffmpeg.writeFile(inputFileName, freshInputData);
        
        console.log('🔧 Re-converting with aggressive compression settings...');
        await this.ffmpeg.exec(aggressiveArgs);
        
        // Read new output
        const newData = await this.ffmpeg.readFile(outputFileName);
        const newBlob = new Blob([newData], { type: 'video/mp4' });
        
        const newConvertedSizeMB = newBlob.size / (1024 * 1024);
        
        // If still larger, use the smaller of the two converted versions
        // (Can't use original as it may not be H.264 format)
        if (newBlob.size > videoFile.size) {
          console.warn(`⚠️ Still larger after aggressive compression (${newConvertedSizeMB.toFixed(2)}MB)`);
          console.warn('⚠️ Original video was already very well compressed');
          console.warn('⚠️ Using the smaller converted version for better compatibility');
          
          // Use the smaller of the two converted versions
          // Both are H.264 format, so pick the smaller one
          if (newBlob.size < blob.size) {
            console.log(`✅ Using more aggressive conversion (${newConvertedSizeMB.toFixed(2)}MB vs ${convertedSizeMB.toFixed(2)}MB)`);
            return newBlob;
          } else {
            console.log(`✅ Using first conversion (${convertedSizeMB.toFixed(2)}MB vs ${newConvertedSizeMB.toFixed(2)}MB)`);
            return blob;
          }
        }
        
        // Use the aggressively compressed version
        const finalReductionPercent = ((1 - newBlob.size / videoFile.size) * 100).toFixed(1);
        const endTime = Date.now();
        const conversionTimeSeconds = ((endTime - startTime) / 1000).toFixed(1);
        const conversionTimeMinutes = (conversionTimeSeconds / 60).toFixed(1);
        console.log(`✅ File size reduced by ${finalReductionPercent}% (${originalSizeMB.toFixed(2)}MB → ${newConvertedSizeMB.toFixed(2)}MB)`);
        console.log(`⏱️ Total conversion time (with re-conversion): ${conversionTimeSeconds}s (${conversionTimeMinutes} minutes)`);
        
        // Cleanup
        try {
          await this.ffmpeg.deleteFile(inputFileName);
          await this.ffmpeg.deleteFile(outputFileName);
        } catch (e) {
          console.warn('⚠️ Error cleaning up temp files:', e);
        }
        
        return newBlob;
      }
      
      // Converted file is smaller - good!
      const reductionPercent = ((1 - blob.size / videoFile.size) * 100).toFixed(1);
      
      if (parseFloat(reductionPercent) > 95) {
        console.warn(`⚠️ WARNING: Extreme size reduction (${reductionPercent}%)`);
        console.warn('⚠️ This might indicate the original video had very high bitrate or resolution');
        console.warn('⚠️ The converted video should still be good quality at 480p');
        console.log(`✅ File size reduced by ${reductionPercent}% (${originalSizeMB.toFixed(2)}MB → ${convertedSizeMB.toFixed(2)}MB)`);
      } else {
        console.log(`✅ File size reduced by ${reductionPercent}% (${originalSizeMB.toFixed(2)}MB → ${convertedSizeMB.toFixed(2)}MB)`);
      }

      // Cleanup
      try {
        await this.ffmpeg.deleteFile(inputFileName);
        await this.ffmpeg.deleteFile(outputFileName);
      } catch (e) {
        console.warn('⚠️ Error cleaning up temp files:', e);
      }

      // Verify converted video resolution
      const convertedResolution = await this.detectVideoResolution(
        new File([blob], 'converted.mp4', { type: 'video/mp4' })
      );
      
      if (convertedResolution) {
        const { width, height } = convertedResolution;
        console.log(`📐 Converted resolution: ${width}x${height}`);
        
        if (height > 480) {
          console.warn(`⚠️ WARNING: Converted video height is ${height}, expected ≤ 480`);
        } else {
          console.log(`✅ Verified: Converted video height is ${height} (≤ 480px)`);
        }
      }

      // Calculate and log conversion time
      const endTime = Date.now();
      const conversionTimeSeconds = ((endTime - startTime) / 1000).toFixed(1);
      const conversionTimeMinutes = (conversionTimeSeconds / 60).toFixed(1);
      
      console.log('✅ Video converted successfully!');
      console.log('📊 Converted size:', (blob.size / (1024 * 1024)).toFixed(2), 'MB');
      console.log(`⏱️ Conversion time: ${conversionTimeSeconds}s (${conversionTimeMinutes} minutes)`);

      return blob;
    } catch (error) {
      console.error('❌ Video conversion error:', error);
      
      // Extract meaningful error message
      const errorMessage = this.extractErrorMessage(error);
      
      // Check for format/codec errors and provide better guidance
      const lowerError = errorMessage.toLowerCase();
      if (lowerError.includes('format') || lowerError.includes('codec') || lowerError.includes('unsupported')) {
        // Try to provide more helpful error message
        throw new Error('Video format or codec issue detected. FFmpeg should support most formats. The file may be corrupted or use a very rare codec. Please try:\n1. Opening the video in a video player to verify it plays\n2. Re-exporting the video from your video editor\n3. Trying a different video file');
      }
      
      // Create a more descriptive error
      const descriptiveError = new Error(errorMessage);
      descriptiveError.originalError = error;
      
      throw descriptiveError;
    }
  }

  /**
   * Cleanup resources
   */
  async cleanup() {
    if (this.ffmpeg) {
      try {
        // FFmpeg.wasm doesn't have explicit cleanup, but we can reset
        this.isLoaded = false;
        this.loadPromise = null;
        this.ffmpeg = null;
      } catch (error) {
        console.warn('⚠️ Error during cleanup:', error);
      }
    }
  }
}

// Export singleton instance
const videoConverter = new VideoConverter();
export default videoConverter;
